import React from 'react';
import { Box, Typography, Link, Stack, IconButton } from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { buildWebSiteUrl } from '../../utils/formatHelpers';

const logoUrl = '/assets/images/logo-white-blue.png';

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const MarketingFooter: React.FC = () => {
  const navLinks = [
    { label: 'For Creators', path: buildWebSiteUrl('/for-creators') },
    { label: 'For Bettors', path: buildWebSiteUrl('/for-bettors') },
    { label: 'Pricing', path: buildWebSiteUrl('/pricing') },
    { label: 'Blog', path: buildWebSiteUrl('/blog') },
    { label: 'About', path: buildWebSiteUrl('/about') },
    { label: 'Investors', path: buildWebSiteUrl('/investors') },
  ];

  const legalLinks = [
    { label: 'Privacy Policy', href: buildWebSiteUrl('/legal/privacy-policy') },
    { label: 'Terms & Conditions', href: buildWebSiteUrl('/legal/terms-and-conditions') },
    { label: 'Contact us', href: 'mailto:contact@moddy.ai?subject=Moddy%20Inquiry' },
    { label: 'Data licensing', href: buildWebSiteUrl('/data-licensing') },
    { label: 'Media & Press', href: buildWebSiteUrl('/media-press') },
  ];

  const socialLinks = [
    { label: 'X (Twitter)', href: 'https://x.com/Moddy_AI', icon: <XIcon /> },
    { label: 'Instagram', href: 'https://www.instagram.com/moddy.ai/', icon: <InstagramIcon /> },
    { label: 'LinkedIn', href: 'https://linkedin.com/company/moddyai', icon: <LinkedInIcon /> },
    { label: 'YouTube', href: 'https://www.youtube.com/@ModdyAI', icon: <YouTubeIcon /> },
  ];

  return (
    <Box
      component="footer"
      sx={{
        maxWidth: 1140,
        width: { xs: '100%', md: 'calc(100% - 48px)' },
        mt: 8,
        mb: { xs: 0, md: 4 },
        mx: 'auto',
        px: { xs: 3, sm: 5, md: 6 },
        py: { xs: 4, sm: 5, md: 6 },
        backgroundColor: '#1e1e1e',
        borderRadius: { xs: 0, md: '24px' },
      }}
    >
      {/* Top section: three columns */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 5, md: 4 }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'flex-start' }}
      >
        {/* Left: Logo + tagline */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ mb: 2 }}>
            <img src={logoUrl} alt="Moddy AI" style={{ height: 56 }} />
          </Box>
          <Typography
            sx={{
              color: 'grey.500',
              fontStyle: 'italic',
              fontSize: '1rem',
              fontWeight: 400,
              maxWidth: 340,
            }}
          >
            Moddy. Where the best models create smarter bets.
          </Typography>
        </Box>

        {/* Center: Nav links - large bold */}
        <Box sx={{ flex: 1 }}>
          {navLinks.map((link) => (
            <Link
              key={link.label}
              href={link.path}
              underline="none"
              sx={{
                color: 'white',
                fontWeight: 700,
                display: 'block',
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                lineHeight: 1.6,
                '&:hover': { color: 'grey.400' },
              }}
            >
              {link.label}
            </Link>
          ))}
        </Box>

        {/* Right: Legal links - smaller */}
        <Box>
          {legalLinks.map((link) => (
            <Link
              key={link.label}
              href={link.href}
              underline="none"
              sx={{
                color: 'white',
                fontWeight: 500,
                display: 'block',
                fontSize: '0.9rem',
                lineHeight: 2,
                '&:hover': { color: 'grey.400' },
              }}
            >
              {link.label}
            </Link>
          ))}
        </Box>
      </Stack>

      {/* Bottom section: social icons + copyright/gambling */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 3, md: 4 }}
        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
        sx={{ mt: { xs: 5, md: 8 } }}
      >
        {/* Social icons */}
        <Stack direction="row" spacing={1}>
          {socialLinks.map((social) => (
            <IconButton
              key={social.label}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={social.label}
              sx={{
                color: 'white',
                border: '1.5px solid rgba(255,255,255,0.3)',
                width: 40,
                height: 40,
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              {social.icon}
            </IconButton>
          ))}
        </Stack>

        {/* Copyright + responsible gambling */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ color: 'grey.500', fontSize: '0.8rem', lineHeight: 1.6 }}>
            © {new Date().getFullYear()} Moddy AI, Inc. All rights reserved.
          </Typography>
          <Typography variant="body2" sx={{ color: 'grey.500', fontSize: '0.8rem', lineHeight: 1.6 }}>
            Gamble responsibly. If you or someone you know has a gambling problem, call 1-800-GAMBLER or visit the National Council on Problem Gambling at{' '}
            <Link
              href="https://www.ncpgambling.org"
              target="_blank"
              rel="noopener"
              sx={{ color: 'white', fontWeight: 700, fontSize: '0.8rem' }}
            >
              ncpgambling.org
            </Link>
            .Moddy is for users 21+ only.
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default MarketingFooter;
